import { memoSanitize } from '@axa-asia/emma-safely-set-inner-html';
import React from 'react';
import './style.scss';
import { resolveLocalizedText } from '../../../../utils';
import Collapsible from '@axa-asia/emma-stylesheet/lib/components/Collapsible/Collapsible';
import loadingIcon from '../../../../assets/images/loading.png';
import moreIcon from '../../../../assets/images/more@3x.png';
import _ from 'lodash';
import moment from 'moment';

export interface InsuredAccessCardProps {
  key: number;
  label: any;
  policy: any;
  policyRiders: Array<any>;
  policyDetail: any;
  isLoading: boolean;
  isExpand?: boolean;
  error: boolean;
  onClick: () => void;
  onClickAuthorize: () => void;
  onClickMenu: () => void;
  onClickDeauthorize: () => void;
  onClickRemindInsured: () => void;
  showDeauthorizeMenu: boolean;
  lastUpdated: string;
  lastDeactivation: string;
}

export const InsuredAccessCard = ({
  key,
  label,
  policy,
  policyRiders,
  policyDetail,
  isLoading,
  isExpand = false,
  error,
  onClick,
  onClickAuthorize,
  onClickMenu,
  onClickDeauthorize,
  onClickRemindInsured,
  showDeauthorizeMenu,
  lastUpdated,
  lastDeactivation,
}: InsuredAccessCardProps) => {
  const {
    deauthorizeLabel,
    ridersLabel,
    authorizeBtn,
    remindInsuredBtn,
    pendingText,
    deactivatingText,
    activatedText,
    policyDetailErrMsg,
  } = label;

  const renderCollapsibleTitle = () => {
    const { policyNumber, policyName } = policy;

    return (
      <div>
        <div>{policyName ? resolveLocalizedText(policyName) : ''}</div>
        {policyNumber && <div className="subtitle">PN: {policyNumber}</div>}
        {!_.isEmpty(policyRiders) && (
          <div>
            <div className="rider-label">
              {ridersLabel ? resolveLocalizedText(ridersLabel) : ''}
            </div>
            <ul className="rider-list">
              {policyRiders.map((rider: any) => (
                <li className="rider-name">
                  {rider.planName ? resolveLocalizedText(rider.planName) : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderCollapsibleContent = () => {
    const { insuredAccessFlag } = policy;
    const { insuredDetails = {} } = policyDetail || {};

    // only display pending layout within 1 hour after authorization/deactivation,
    // system assumed that API will return correct status within 1 hour
    const isActivating =
      lastUpdated &&
      moment() < moment(lastUpdated, ['YYYY-MM-DDTHH-mm-ss']).add(1, 'hour');
    const isDeactivating =
      lastDeactivation &&
      moment() <
        moment(lastDeactivation, ['YYYY-MM-DDTHH-mm-ss']).add(1, 'hour');
    const isPending = isActivating || isDeactivating;

    if (isLoading) {
      return (
        <div className="loading-wrapper">
          <img src={loadingIcon} alt="loading" />
        </div>
      );
    }

    return error ? (
      <div className="error-wrapper">
        {policyDetailErrMsg ? resolveLocalizedText(policyDetailErrMsg) : ''}
      </div>
    ) : (
      <div className="insured-wrapper">
        <div className="insured-row">
          <div className="insured-name">{insuredDetails.lastName}</div>
          {insuredAccessFlag !== 'Y' && !isPending && (
            <button
              className="emma-stylesheet-primary-default-button"
              data-enable-ga
              data-ga-type="button"
              onClick={onClickAuthorize}
            >
              {authorizeBtn ? resolveLocalizedText(authorizeBtn) : ''}
            </button>
          )}
          {isActivating && (
            <button
              className="emma-stylesheet-primary-ghost-button"
              onClick={onClickRemindInsured}
              data-enable-ga
              data-ga-type="button"
            >
              {remindInsuredBtn ? resolveLocalizedText(remindInsuredBtn) : ''}
            </button>
          )}
          {insuredAccessFlag === 'Y' && !isPending && (
            <div className="authorized-wrapper">
              <button
                className="emma-stylesheet-primary-ghost-button"
                onClick={onClickRemindInsured}
                data-enable-ga
                data-ga-type="button"
              >
                {remindInsuredBtn ? resolveLocalizedText(remindInsuredBtn) : ''}
              </button>
              <button
                className="unauthorize-btn"
                data-enable-ga
                data-ga-type="button"
                data-ga-text="more"
                onClick={onClickMenu}
              >
                <img className="more-icon" src={moreIcon} alt="more" />
              </button>
            </div>
          )}
        </div>
        {isPending && (
          <div
            className="pending-text"
            dangerouslySetInnerHTML={{
              __html: memoSanitize(
                isActivating
                  ? pendingText
                    ? resolveLocalizedText(pendingText)
                    : ''
                  : deactivatingText
                  ? resolveLocalizedText(deactivatingText)
                  : '',
              ),
            }}
          ></div>
        )}
        {insuredAccessFlag === 'Y' && !isPending && (
          <div
            className="pending-text"
            dangerouslySetInnerHTML={{
              __html: memoSanitize(
                activatedText ? resolveLocalizedText(activatedText) : '',
              ),
            }}
          ></div>
        )}
      </div>
    );
  };

  const renderDeauthorizeMenu = () => (
    <div className="unauthorize-menu">
      <button
        className="menu-btn"
        data-enable-ga
        data-ga-type="button"
        onClick={onClickDeauthorize}
      >
        {deauthorizeLabel ? resolveLocalizedText(deauthorizeLabel) : ''}
      </button>
    </div>
  );

  const { policyName } = policy;

  return (
    <div key={key} className="collapsible-wrapper" onClick={onClick}>
      <Collapsible
        title={renderCollapsibleTitle()}
        isExpand={isExpand}
        dataGaTitleText={resolveLocalizedText(policyName)}
      >
        {renderCollapsibleContent()}
      </Collapsible>
      {showDeauthorizeMenu && renderDeauthorizeMenu()}
    </div>
  );
};
